import type { Component } from 'vue'
import customDate from '../components/customDate.vue'
import customInput from '../components/customInput.vue'
import customRadio from '../components/customRadio.vue'
import customSelect from '../components/customSelect.vue'
import customSwitch from '../components/customSwitch.vue'
import CustomDocUpload from '../components/upload/CustomDocUpload.vue'
import CustomImageUpload from '../components/upload/CustomImageUpload.vue'
import CustomUpload from '../components/upload/CustomUpload.vue'

export interface FormComponent {
  component: Component
  props?: Record<string, any>
  supportedProps?: string[] // 定义该组件支持哪些props
  defaultProps?: Record<string, any>
  typeConfig?: Record<string, any> // 组件类型特定配置
}

const UPLOAD_SUPPORTED_PROPS = [
  'multiple',
  'accept',
  'maxFileCount',
  'maxFileSize',
  'listType',
  'action',
  'fileKey',
  'isCustomRequest',
  'uploadBtnTitle',
  'uploadBtnConfig',
  'showFileList',
  'directoryDnd',
  'disabled',
  'readonly',
  'placeholder',
  'isFileId',
  'isFileUrl',
  'onChange',
  'onBeforeUpload',
  'onFinish',
  'onError',
  'onRemove'
]
const BASE_UPLOAD_DEFAULT_PROPS = {
  multiple: false,
  isCustomRequest: true,
  showFileList: true,
  isFileId: true
}
const INPUT_COMPONENT_CONFIG: FormComponent = {
  component: customInput,
  supportedProps: [
    'type',
    'placeholder',
    'clearable',
    'maxlength',
    'showPassword',
    'showPasswordOn',
    'bordered',
    'onInput',
    'prefixIcon',
    'prefixIconSize',
    'size'
  ],
  defaultProps: {
    clearable: true,
    bordered: true
  }
}

export type ComponentType =
  | 'input'
  | 'password'
  | 'textarea'
  | 'select'
  | 'radio'
  | 'date'
  | 'switch'
  | 'uploadImage'
  | 'file'
  | 'uploadDoc'
  | 'upload'
  | 'slot'

const componentRegistry: Record<ComponentType, FormComponent> = {
  input: INPUT_COMPONENT_CONFIG, // 直接赋值
  password: INPUT_COMPONENT_CONFIG,
  textarea: INPUT_COMPONENT_CONFIG,
  select: {
    component: customSelect,
    supportedProps: [
      'options',
      'multiple',
      'filterable',
      'placeholder',
      'clearable',
      'labelField',
      'valueField',
      'size'
    ],
    defaultProps: { clearable: true, filterable: true }
  },
  upload: {
    component: CustomUpload,
    supportedProps: UPLOAD_SUPPORTED_PROPS,
    defaultProps: BASE_UPLOAD_DEFAULT_PROPS
  },
  radio: {
    component: customRadio,
    supportedProps: ['options', 'placeholder', 'labelField', 'valueField'],
    defaultProps: {}
  },
  date: {
    component: customDate,
    supportedProps: ['type', 'placeholder', 'clearable', 'format', 'disabled'],
    defaultProps: { clearable: true, type: 'date' }
  },
  switch: {
    component: customSwitch,
    supportedProps: ['checkedValue', 'uncheckedValue', 'size'],
    defaultProps: { size: 'medium' }
  },
  uploadImage: {
    component: CustomImageUpload,
    supportedProps: UPLOAD_SUPPORTED_PROPS,
    defaultProps: { ...BASE_UPLOAD_DEFAULT_PROPS, listType: 'image' }
  },
  file: {
    component: CustomUpload,
    supportedProps: UPLOAD_SUPPORTED_PROPS,
    defaultProps: BASE_UPLOAD_DEFAULT_PROPS
  },
  uploadDoc: {
    component: CustomDocUpload,
    supportedProps: UPLOAD_SUPPORTED_PROPS,
    defaultProps: { ...BASE_UPLOAD_DEFAULT_PROPS, listType: 'text' }
  },
  slot: {
    component: {
      template: '<slot :name="slotName" v-bind="slotProps" />',
      props: ['slotName', 'formData', 'formItem', 'modelValue']
    },
    defaultProps: {}
  }
}
export function getSupportedProps(type: ComponentType): string[] {
  return componentRegistry[type]?.supportedProps || []
}
export function filterComponentProps(
  type: ComponentType,
  props: Record<string, any>
): Record<string, any> {
  const supportedProps = getSupportedProps(type)
  const defaultProps = componentRegistry[type]?.defaultProps || {}
  const filtered: Record<string, any> = {}
  Object.assign(filtered, defaultProps)
  supportedProps.forEach((key) => {
    if (props[key] !== undefined) filtered[key] = props[key]
  })
  return filtered
}
export function getComponent(type: ComponentType): FormComponent | undefined {
  return componentRegistry[type]
}
export function registerComponent(
  type: ComponentType,
  config: FormComponent
): void {
  componentRegistry[type] = config
}
export function getComponentTypes(): ComponentType[] {
  return Object.keys(componentRegistry) as ComponentType[]
}
